/**
 * M5 NL layer — shared engine types. These sit between the closed NL contract
 * (`@/lib/contracts/nl`: the versioned `map_op` + filter-AST enums) and M4's
 * declarative artifact (`MapSpec`). The invariant (M5.md): the persisted
 * representation of an NL-authored map is structured ops + saved views, NEVER
 * the prompt or the model response — so re-render is a pure fold with zero LLM
 * cost. The LLM only *plans* (produces these structures); the deterministic
 * engine in `query.ts` *executes*.
 */

import type { FilterNode } from "@/lib/contracts/nl";
import type { PointColumn } from "@/lib/map/pointset";

/** A resolved place anchor (M2 geocoder output), shown to the user and editable. */
export interface Anchor {
  /** The raw phrase the user typed ("near Dallas", "75201"). */
  query: string;
  lat: number;
  lng: number;
  /** Human label for the echo chip ("Dallas, TX"). */
  label: string;
  /** 0..1 geocode confidence, or 1 for a passthrough coordinate. */
  confidence: number;
  /** How the phrase was interpreted: a point, or a point+radius approximation of a ZIP/county. */
  kind: "point" | "zip_approx" | "region_approx";
}

/** Aggregate functions the deterministic query engine supports (v1 grammar). */
export type AggregateFn = "count" | "sum" | "avg" | "min" | "max";

/** A spatial primitive over the loaded point set (v1 grammar). */
export type SpatialKind = "within_radius" | "nearest_n" | "distance_to";

/**
 * A structured, deterministic query plan. Produced by the planner (LLM) and
 * executed entirely by `runQuery` over the loaded points — the model never
 * computes an answer. Every field references a *resolved* column name (post
 * fuzzy-resolution) or a *resolved* anchor, never a raw user phrase.
 */
export interface QueryPlan {
  /** Attribute predicate applied before aggregation/spatial (closed grammar). */
  filter?: FilterNode;
  /** Tabular aggregate; omit for a pure filter/highlight query. */
  aggregate?: {
    fn: AggregateFn;
    /** Column to aggregate; omitted/ignored for `count`. */
    column?: string;
    /** Single group-by column (v1 supports one). */
    groupBy?: string;
  };
  /** Ranking: return the top N by a numeric column (desc unless `asc`). */
  topN?: { column: string; n: number; asc?: boolean };
  /** Spatial primitive; mutually composable with `filter`. */
  spatial?: {
    kind: SpatialKind;
    anchor: Anchor;
    /** Radius in kilometres for `within_radius`. */
    km?: number;
    /** N for `nearest_n`. */
    n?: number;
  };
}

/** How a QUERY result should affect the map (the "map effect" of the Q&A triple). */
export type MapEffect =
  | { kind: "highlight"; ids: string[] }
  | { kind: "filter_to"; ids: string[] }
  | { kind: "fit"; ids: string[] }
  | { kind: "annotate"; annotations: Array<{ id: string; text: string }> }
  | { kind: "none" };

/** The deterministic engine's output for a QUERY. */
export interface QueryResult {
  /** Plain-English answer, computed by the engine (never the LLM). */
  answerText: string;
  /** The map effect to apply (highlight / filter-to / fit / annotate). */
  effect: MapEffect;
  /** Row ids the query matched, for the interpretation echo + promote-to-view. */
  matchedIds: string[];
  /** Per-group aggregate rows, when the plan grouped. */
  groups?: Array<{ group: string; value: number; count: number }>;
}

/**
 * The per-request LLM context. Built from schema ONLY — column names, inferred
 * types, cardinality, and (for low-cardinality categoricals) capped distinct
 * values. NEVER includes row values and is never persisted. (M5.md cost + PII
 * controls, AC-5.)
 */
export interface LLMContext {
  columns: Array<{
    name: string;
    type: PointColumn["type"];
    cardinality: number;
    /** Present only for low-cardinality categoricals; capped at 50. */
    distinctValues?: string[];
  }>;
  rowCount: number;
}

/** Max distinct values surfaced per low-cardinality categorical column. */
export const MAX_DISTINCT_IN_CONTEXT = 50;
